import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import { useGetMarinaSubscriptionTransactionsListQuery, useGetMarinaSubscriptionTransactionHarborsQuery } from '@/services/SubscriptionTransactionsService';
import {
  MarinaSubscriptionTransactionItem,
  MarinaSubscriptionTransactionsListParams,
} from '@/types/subscription-transactions.types';
import { TransactionStatus } from '@/types/transactions.types';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDateTime } from '@/utils/Date';

const TRANSACTION_STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  PENDING: 'Pending',
};

const SubscriptionTransactions = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [dateRange, setDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [harborFilter, setHarborFilter] = useState<string>('all');

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const { data: harbors = [] } = useGetMarinaSubscriptionTransactionHarborsQuery();

  const apiStart = dateRange[0]
    ? moment(dateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;
  const apiEnd = dateRange[1]
    ? moment(dateRange[1], 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;

  const listParams: MarinaSubscriptionTransactionsListParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    start_date: apiStart,
    end_date: apiEnd,
    status:
      statusFilter !== 'all' ? (statusFilter as TransactionStatus) : undefined,
    harbor_id: harborFilter !== 'all' ? harborFilter : undefined,
  };

  const {
    data: list,
    isFetching: listLoading,
    error: listError,
  } = useGetMarinaSubscriptionTransactionsListQuery(listParams);

  useEffect(() => {
    if (harborFilter === 'all') return;
    if (!harbors.some((harbor) => harbor.id === harborFilter)) {
      setHarborFilter('all');
    }
  }, [harborFilter, harbors]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [dateRange, statusFilter, harborFilter, setPagination]);

  const rows: MarinaSubscriptionTransactionItem[] = useMemo(
    () => list?.items ?? [],
    [list]
  );

  useEffect(() => {
    if (list) {
      handleTotalPages(list.totalPages || 1);
      handleTotalItems(list.totalItems || 0);
    }
  }, [list, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    if (!listError) return;
    const err = listError as unknown as {
      data?: unknown;
      error?: string;
    } | null;
    let message = 'Failed to fetch subscription transactions';
    if (
      err?.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err?.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [listError, enqueueSnackbar]);

  const columns = [
    {
      accessorFn: (row: MarinaSubscriptionTransactionItem) => row.harbor_name,
      id: 'harbor',
      header: 'Harbor',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <span>{row.original.harbor_name || '—'}</span>
      ),
    },
    {
      accessorFn: (row: MarinaSubscriptionTransactionItem) =>
        row.subscription_name,
      id: 'plan',
      header: 'Plan',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <span
          className="line-clamp-2"
          dangerouslySetInnerHTML={{
            __html: row.original.subscription_name || '—',
          }}
        />
      ),
    },
    {
      accessorFn: (row: MarinaSubscriptionTransactionItem) => row.status,
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <span
          className={`badge ${row.original.status === 'SUCCEEDED' ? 'badge-success' : row.original.status === 'FAILED' ? 'badge-danger' : 'badge-warning'}`}
        >
          {TRANSACTION_STATUS_LABELS[row.original.status] || row.original.status}
        </span>
      ),
    },
    {
      accessorFn: (row: MarinaSubscriptionTransactionItem) => row.amount,
      id: 'amount',
      header: 'Amount',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <span>
          {formatFinnishCurrency(row.original.amount, {
            currency: row.original.currency,
          })}
        </span>
      ),
    },
    {
      accessorFn: (row: MarinaSubscriptionTransactionItem) =>
        row.netvisor_invoice_id,
      id: 'invoice',
      header: 'Invoice Ref',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <span className="text-xs font-mono break-all">
          {row.original.netvisor_invoice_id || '—'}
        </span>
      ),
    },
    {
      accessorFn: (row: MarinaSubscriptionTransactionItem) => row.created_at,
      id: 'created_at',
      header: 'Date',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <span className="text-sm">
          {formatDisplayDateTime(new Date(row.original.created_at))}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: { row: { original: MarinaSubscriptionTransactionItem } }) => (
        <button
          onClick={() =>
            navigate(`/subscription-transactions/${row.original.id}`)
          }
          className="btn btn-sm btn-ghost gap-2"
          title="View transaction details"
        >
          <KeenIcon icon="arrow-right" className="text-lg" />
          View
        </button>
      ),
    },
  ];

  return (
    <Container>
      <div className="flex items-center justify-between mb-6">
        <h1
          id="subscription-transactions-heading"
          className="text-xl font-semibold"
          tabIndex={-1}
        >
          Subscription Transactions
        </h1>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col xl:flex-row xl:items-end gap-4">
            <div className="flex-1">
              <label className="form-label text-sm">Search</label>
              <div className="relative">
                <KeenIcon
                  icon="magnifier"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  className="input input-bordered w-full pl-10"
                  placeholder="Search by ID, plan, harbor, invoice ref..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </div>

            <div className="w-full xl:w-56">
              <label htmlFor="filter-harbor" className="form-label text-sm">
                Harbor
              </label>
              <select
                id="filter-harbor"
                aria-label="Filter by harbor"
                className="select select-bordered w-full"
                value={harborFilter}
                onChange={(e) => setHarborFilter(e.target.value)}
              >
                <option value="all">All harbors</option>
                {harbors.map((harbor) => (
                  <option key={harbor.id} value={harbor.id}>
                    {harbor.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full xl:w-44">
              <label htmlFor="filter-status" className="form-label text-sm">
                Status
              </label>
              <select
                id="filter-status"
                aria-label="Filter by status"
                className="select select-bordered w-full"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All statuses</option>
                {Object.entries(TRANSACTION_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full xl:w-72">
              <label className="form-label text-sm">Date range</label>
              <DateRangePicker
                startDateName="start_date"
                endDateName="end_date"
                isMandatory={false}
                label={undefined}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                allowPastDates={true}
                allowFutureDates={false}
                className="w-full"
              />
            </div>

            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                resetSearch();
                setDateRange([undefined, undefined]);
                setStatusFilter('all');
                setHarborFilter('all');
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <DataGrid
            columns={columns}
            data={rows}
            rowSelect={false}
            loading={listLoading}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: '{from} - {to} of {count}',
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
            hasActiveFilters={
              !!searchTerm ||
              !!dateRange[0] ||
              !!dateRange[1] ||
              statusFilter !== 'all' ||
              harborFilter !== 'all'
            }
            onFilterReset={() => {
              resetSearch();
              setDateRange([undefined, undefined]);
              setStatusFilter('all');
              setHarborFilter('all');
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
          />
        </div>
      </div>
    </Container>
  );
};

export default SubscriptionTransactions;
